"use client";

import {
  QUALITY_LABEL,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { useChordLabel } from "@/hooks/use-notation";
import {
  BORDER,
  FONT_MONO,
  GREEN,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET_BRIGHT,
  VIOLET_SOFT,
} from "./palette";
import {
  ChordPracticeModeToggle,
  type ChordPracticeMode,
} from "./chord-practice-mode-toggle";

type Props = {
  entry: ChordEntry;
  positions: number;
  staticDone: number;
  mode: ChordPracticeMode;
  onModeChange?: (mode: ChordPracticeMode) => void;
  onClose?: () => void;
};

export function ChordPracticeTempoHeader({
  entry,
  positions,
  staticDone,
  mode,
  onModeChange,
  onClose,
}: Props) {
  const chordLabel = useChordLabel();

  return (
    <header
      className="relative z-10 flex shrink-0 items-center justify-between"
      style={{
        padding: "14px 24px",
        borderBottom: `1px solid ${BORDER}`,
        background: "#0a0a0e",
      }}
    >
      <div className="flex items-center" style={{ gap: 14 }}>
        <span
          style={{
            fontSize: 26,
            fontWeight: 200,
            lineHeight: 1,
            letterSpacing: "-0.04em",
            fontFamily: "Inter, sans-serif",
            background: `linear-gradient(180deg, ${TEXT} 0%, ${TEXT_DIM} 90%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {chordLabel(entry.symbol)}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            backgroundColor: VIOLET_SOFT,
            color: VIOLET_BRIGHT,
            fontFamily: FONT_MONO,
            padding: "4px 8px",
            borderRadius: 6,
            lineHeight: 1,
          }}
        >
          {QUALITY_LABEL[entry.quality]}
        </span>
      </div>

      <div
        className="flex items-center"
        style={{ gap: 12, fontFamily: FONT_MONO, fontSize: 11 }}
      >
        {onModeChange && (
          <ChordPracticeModeToggle mode={mode} onChange={onModeChange} />
        )}
        <span style={{ color: TEXT_MUTE }}>POSITIONS</span>
        <span
          style={{
            color: staticDone >= positions ? GREEN : TEXT_DIM,
            fontWeight: 600,
          }}
        >
          {staticDone} / {positions}
          {staticDone >= positions ? " ✓" : ""}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full p-1.5 transition-colors hover:bg-white/[0.06]"
            style={{ color: TEXT_DIM, marginLeft: 4 }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        )}
      </div>
    </header>
  );
}
